import { Injectable } from '@angular/core';
import { ApiService } from '../../core/api.service';
import { environment } from '../../../environments/environment';
import { BlobService } from '../../core/blob.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CidadeDto } from '../cidades/cidade.model';

export interface FornecedorListDto {
  id: string;
  nome: string;
  slug: string;
  /** Primary (legacy) single-city string kept for backwards-compat display */
  cidade?: string;
  estado?: string;
  cnpj?: string;
  email?: string;
  telefone?: string;
  rating?: number;
  destaque?: boolean;
  publicado?: boolean;
  isClaimed?: boolean;
  seloFornecedor?: boolean;
  /** Multi-city: array of cities this supplier serves */
  cidades?: CidadeDto[];
}

@Injectable({ providedIn: 'root' })
export class FornecedoresService {
  constructor(private api: ApiService, private blob: BlobService) {}

  list(page = 1, pageSize = 12, filters?: any): Observable<{ items: FornecedorListDto[]; total: number }> {
    const params = { page, pageSize, ...(filters || {}) };
    return this.api.get('/admin/fornecedores', params).pipe(
      // Normaliza a resposta para sempre retornar items e total
      map((res: any) => ({
        items: res.items || res.data || [],
        total: res.total || res.meta?.total || 0
      }))
    );
  }

  getBySlug(slug: string) { return this.api.get(`/admin/fornecedores/slug/${slug}`); }

  // Get fornecedor by id (admin endpoint)
  get(id: string) { return this.api.get(`/admin/fornecedores/${id}`); }

  // Admin get by id (ensures access to full admin data)
  adminGet(id: string) { return this.api.get(`/admin/fornecedores/${id}`); }

  // Admin list with advanced filters - maps UI parameters to backend API contract
  adminList(params: {
    page?: number;
    pageSize?: number;
    // Basic filters
    nome?: string;
    categoriaId?: string;
    cidade?: string;
    /** Filter by city ID (multi-city) */
    cidadeId?: string;
    planLevel?: 'Free' | 'Vitrine' | string;
    ativo?: boolean;
    publicado?: boolean;
    // Advanced filters
    destaque?: string; // "todos", "sim", "nao"
    claimed?: boolean;
    leadsMinimo?: number;
    vencendoEmDias?: number;
    // Sorting
    orderBy?: 'nome' | 'criacao' | 'atualizacao' | 'leads' | 'vencimento' | string;
    orderDirection?: 'asc' | 'desc' | string;
    // Backward compatibility (maps to new parameters)
    categoria?: string;
    plano?: string;
    isClaimed?: boolean;
    minLeads?: number;
    vencendoEm?: number;
    sort?: string;
    order?: string;
  } = {}) {
    // Map old parameters to new backend API contract
    const mapped: any = {
      page: params.page ?? 1,
      pageSize: Math.min(params.pageSize ?? 20, 100), // Max 100
      // Map basic filters
      nome: params.nome,
      categoriaId: params.categoriaId || params.categoria,
      cidade: params.cidade,
      cidadeId: params.cidadeId,
      planLevel: params.planLevel || params.plano,
      ativo: params.ativo,
      publicado: params.publicado,
      // Map advanced filters
      destaque: params.destaque,
      claimed: params.claimed !== undefined ? params.claimed : params.isClaimed,
      leadsMinimo: params.leadsMinimo ?? params.minLeads,
      vencendoEmDias: params.vencendoEmDias ?? params.vencendoEm,
      // Map sorting
      orderBy: params.orderBy || (this.mapLegacySortField(params.sort) || 'atualizacao'),
      orderDirection: params.orderDirection || params.order || 'desc'
    };

    // Remove undefined values to keep query string clean
    Object.keys(mapped).forEach(key => {
      if (mapped[key] === undefined || mapped[key] === null || mapped[key] === '') {
        delete mapped[key];
      }
    });

    return this.api.get('/admin/fornecedores', mapped);
  }

  /**
   * Maps legacy sort field names to new backend API field names
   * @param legacySort Old sort field name (nome, leads, rating, updatedAt)
   * @returns New sort field name (nome, criacao, atualizacao, leads, vencimento)
   */
  private mapLegacySortField(legacySort?: string): string | undefined {
    if (!legacySort) return undefined;
    const map: { [key: string]: string } = {
      'nome': 'nome',
      'leads': 'leads',
      'rating': 'atualizacao', // No rating in new API, use atualizacao
      'updatedAt': 'atualizacao',
      'atualizacao': 'atualizacao',
      'criacao': 'criacao',
      'vencimento': 'vencimento'
    };
    return map[legacySort] || 'atualizacao';
  }

  create(payload: any) { return this.api.post('/admin/fornecedores', payload); }

  update(id: string, payload: any) { return this.api.put(`/admin/fornecedores/${id}`, payload); }

  delete(id: string) { return this.api.delete(`/admin/fornecedores/${id}`); }

  // Toggle ativo/inativo (soft delete)
  toggleActive(id: string): Observable<any> {
    // PATCH na rota real do backend
    return this.api.patch(`/admin/fornecedores/${id}/toggle-active`, {});
  }

  // Resetar contador de leads (para planos Free)
  // Note: Both resetLeadCount and resetLeads exist for backward compatibility
  // They call the same endpoint
  resetLeadCount(id: string): Observable<any> {
    return this.api.patch(`/admin/tier/fornecedores/${id}/reset-leads`, {});
  }

  resetLeads(id: string): Observable<any> {
    return this.resetLeadCount(id);
  }

  // Media endpoints
  getMedia(fornecedorId: string) { return this.api.get(`/admin/fornecedores/${fornecedorId}/images`); }

  uploadMedia(formData: FormData) {
    return this.blob.uploadMedia(formData);
  }

  uploadMediaWithCancel(formData: FormData, fornecedorId: string, opts?: { retries?: number; backoffMs?: number }) {
    const apiBase = environment.API_BASE_URL?.replace(/\/$/, '') ?? '/api/v1';
    return this.blob.uploadWithCancel(`${apiBase}/admin/fornecedores/${fornecedorId}/images`, formData, opts);
  }

  deleteMedia(id: string) { return this.api.delete(`/media/${id}`); }

  // Mark a media as primary using the media service endpoint
  markPrimary(mediaId: string) { return this.api.post(`/media/${mediaId}/mark-primary`, {}); }

  // Contact endpoint - admin only
  contactFornecedor(fornecedorId: string, payload: any) {
    return this.api.post(`/admin/fornecedores/${fornecedorId}/contact`, payload);
  }

  // Get supplier user linked to fornecedor
  getSupplierUser(fornecedorId: string) {
    const url = `/admin/fornecedores/${fornecedorId}/usuario`;
    console.log('[FornecedoresService] GET:', url);
    return this.api.get(url);
  }

  // Resend welcome email to supplier
  resendWelcomeEmail(fornecedorId: string) {
    const url = `/admin/suppliers/${fornecedorId}/send-welcome`;
    console.log('[FornecedoresService] POST:', url);
    return this.api.post(url, {});
  }

  // Admin actions
  // Plan override: change plan level manually with optional expiry
  overridePlan(id: string, payload: { planLevel: 'Free' | 'Vitrine' | string; expiryDate?: string; reason?: string }) {
    return this.api.patch(`/admin/tier/fornecedores/${id}/override-plan`, payload);
  }

  editSubscriptionDates(id: string, payload: {
    vitrineSinceDate: string;
    subscriptionExpiryDate: string;
    valorMensal?: number;
    reason?: string;
  }) {
    return this.api.patch(`/admin/fornecedores/${id}/subscription-dates`, payload);
  }

  setDestaque(id: string, payload: { destaque: boolean; expiryDate?: string }) {
    return this.api.patch(`/admin/fornecedores/${id}/destaque`, payload);
  }

  // Get leads for a specific fornecedor
  getLeads(id: string, params: { page?: number; pageSize?: number } = {}): Observable<any> {
    const p = { page: 1, pageSize: 20, ...(params || {}) };
    return this.api.get(`/admin/fornecedores/${id}/leads`, p);
  }
}
